import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useForm } from 'react-hook-form';
import {
    Container,
    Typography,
    Button,
    Box,
    TextField,
    Paper,
    Grid,
    Alert,
    Avatar,
    CircularProgress
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import { createCallRequest } from '../actions/callRequestActions';
import StoreHeader from './StoreHeader';
import CustomerLogin from './CustomerLogin';
import { fetchStores } from '../actions/storeActions';
import { useStoreHours } from '../hooks/useStoreHours';
import StoreHours from './StoreHours';

const CreateCallRequest = ({ createCallRequest, callRequest, customer, fetchStores, store, isFetchingStores }) => {
    const navigate = useNavigate();
    const { storeId } = useParams();
    const { register, handleSubmit, reset, formState: { errors } } = useForm();
    const isStoreOpen = useStoreHours(store);

    // login is mandatory; identity comes from the account and the form only
    // appears (prefilled) when the profile is missing a field
    const isProfileComplete = !!(customer && customer.email && customer.name && customer.lastName);

    const onSubmit = (data) => {
        const { email, name, lastName } = data;
        createCallRequest(storeId, email, name, lastName);
    };

    const onConfirm = () => {
        createCallRequest(storeId);
    };

    useEffect(() => {
        if(!store) {
            fetchStores();
        }
    }, [store, fetchStores]);

    useEffect(() => {
        if (customer) {
            reset({
                email: customer.email || '',
                name: customer.name || '',
                lastName: customer.lastName || '',
            });
        }
    }, [customer, reset]);

    useEffect(() => {
        if (callRequest.callRequest) {
            navigate('/waiting-room');
        }
    }, [callRequest.callRequest, navigate]);

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            {!store || isFetchingStores ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <CircularProgress />
                </Box>
            ) : (
            <>
            <StoreHeader store={store} />

            {!customer ? (
                <Box sx={{ mt: 3 }}>
                    <CustomerLogin
                        title="Iniciá sesión para llamar"
                        subtitle="Para ingresar a la sala de espera necesitás una cuenta. Usá Google o Apple, es gratis."
                    />
                </Box>
            ) : (
            <Paper elevation={3} sx={{ p: 4, mt: 3 }}>
                <Typography variant="h4" component="h1" align="center" gutterBottom>
                    Ingresar a sala de espera
                </Typography>

                {!isStoreOpen && (
                    <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                        La tienda está cerrada en este momento. Por favor, intenta más tarde durante el horario de atención.
                    </Alert>
                )}

                <Box sx={{ mt: 3 }}>
                    <Box sx={{ mb: 3 }}>
                        <StoreHours store={store} />
                    </Box>
                    {isStoreOpen && (
                        isProfileComplete ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Avatar
                                        src={customer.profileImageUrl || undefined}
                                        imgProps={{ referrerPolicy: 'no-referrer' }}
                                    >
                                        {customer.name ? customer.name[0] : '?'}
                                    </Avatar>
                                    <Box>
                                        <Typography>
                                            {customer.name} {customer.lastName}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {customer.email}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    size="large"
                                    sx={{ mt: 1, borderRadius: 2 }}
                                    onClick={onConfirm}
                                >
                                    Ingresar a sala de espera
                                </Button>
                            </Box>
                        ) : (
                        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                            <Alert severity="info" sx={{ mb: 3 }}>
                                Completá los datos que faltan de tu perfil para ingresar.
                            </Alert>
                            <Grid container spacing={3}>
                                <Grid size={12}>
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        type="email"
                                        InputLabelProps={{ shrink: true }}
                                        {...register("email", {
                                            required: "El email es requerido",
                                            pattern: {
                                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                message: "Email inválido"
                                            }
                                        })}
                                        error={!!errors.email}
                                        helperText={errors.email?.message}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Nombre"
                                        type="text"
                                        InputLabelProps={{ shrink: true }}
                                        {...register("name", { required: "El nombre es requerido" })}
                                        error={!!errors.name}
                                        helperText={errors.name?.message}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Apellido"
                                        type="text"
                                        InputLabelProps={{ shrink: true }}
                                        {...register("lastName", { required: "El apellido es requerido" })}
                                        error={!!errors.lastName}
                                        helperText={errors.lastName?.message}
                                    />
                                </Grid>

                                <Grid size={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        size="large"
                                        sx={{ mt: 2, borderRadius: 2 }}
                                    >
                                        Ingresar a sala de espera
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                        )
                    )}
                </Box>
            </Paper>
            )}
            </>
            )}
        </Container>
    );
};

const mapStateToProps = (state, ownProps) => {
    return {
        callRequest: state.callRequest,
        customer: state.customer.customer,
        store: state.stores.stores[ownProps.storeId],
        isFetchingStores: state.stores.isFetching
    };
};

export default connect(
    mapStateToProps,
    { createCallRequest, fetchStores }
)(CreateCallRequest);
